import React, { forwardRef, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import { useRecoilValue } from 'recoil';

import { dailyDayForecastTileTestIDs, Theme } from '@constants';
import { dayDateTimeState } from '@store';

import { roundNearest5 } from '../../../../helpers/DataFormatting';
import { displayString, isDisplayable, missingValue } from '../../../../helpers/StringUtils';
import { TimeUtils } from '../../../../helpers/TimeUtils';
import { FocusableHighlight } from '../../../focusable/FocusableHighlight/FocusableHighlight';
import { TWCIcon } from '../../../primitives/TWCIcon/TWCIcon';
import { TWCText } from '../../../primitives/TWCText/TWCText';

const tPath = 'dailyDayForecastTile.';

const ForecastTileContent = memo(
  ({ dayName, date, iconCodeExtend, dayTemp, nightTemp, precipChance, isLoading, error, focused = false }) => {
    const { t } = useTranslation();

    return (
      <View importantForAccessibility={'no-hide-descendants'} style={styles.focusItemsContainer}>
        <View style={focused ? styles.topArrowFocus : styles.topArrowHidden} />
        <View style={focused ? styles.containerFocus : styles.container}>
          {isLoading ? (
            <View style={styles.centerContainer}>
              <ActivityIndicator noTestID color={Theme.loading} size={'large'} />
            </View>
          ) : error ? (
            <View style={styles.centerContainer}>
              <TWCText noTestID style={styles.errorText} variant={'regularText'}>
                {'An error occured.'}
              </TWCText>
            </View>
          ) : (
            <>
              <TWCText
                style={styles.tileHeader}
                testID={dailyDayForecastTileTestIDs.dayNameText}
                variant={'regularText36'}
              >
                {displayString(dayName, error)}
              </TWCText>
              <TWCText
                style={styles.tileSubheader}
                testID={dailyDayForecastTileTestIDs.dateText}
                variant={'regularText24Transparent'}
              >
                {displayString(date, error)}
              </TWCText>
              <TWCIcon code={iconCodeExtend} height={78} testID={dailyDayForecastTileTestIDs.icon} width={78} />
              <TWCText
                style={[
                  styles.tempText,
                  dayTemp > 0 && {
                    paddingLeft: Theme.px(SIZES.degreeSymbolWidthLg),
                  },
                ]}
                testID={dailyDayForecastTileTestIDs.dayTempText}
                variant={'regularText66'}
              >
                {isDisplayable(dayTemp, error) ? `${dayTemp}˚` : missingValue}
              </TWCText>
              <TWCText
                style={[
                  styles.nightTemp,
                  nightTemp > 0 && {
                    paddingLeft: Theme.px(SIZES.degreeSymbolWidthSm),
                  },
                ]}
                testID={dailyDayForecastTileTestIDs.nightTempText}
                variant={'regularText30'}
              >
                {isDisplayable(nightTemp, error) ? `${nightTemp}˚` : missingValue}
              </TWCText>
              <View style={styles.precipContainer}>
                <TWCText testID={dailyDayForecastTileTestIDs.precipLabel} variant={'regularText24Transparent'}>
                  {t(`${tPath}precipLabel`)}
                </TWCText>
                <TWCText
                  style={styles.precipText}
                  testID={dailyDayForecastTileTestIDs.precipText}
                  variant={'regularText24Transparent'}
                >
                  {isDisplayable(precipChance, error) ? `${precipChance}%` : missingValue}
                </TWCText>
              </View>
            </>
          )}
        </View>
        <TWCText
          style={focused ? styles.viewHourlyIndicatorFocused : styles.viewHourlyIndicator}
          testID={dailyDayForecastTileTestIDs.viewHourlyText}
          variant={'tileSubheader'}
        >
          {t(`${tPath}viewHourlyLabel`)}
        </TWCText>
      </View>
    );
  },
);

/**
 * Tile to use on the daily forecast dashboard. Each tile is one day in the 7 day forecast swimlane.
 * When dayName is not null, that will be used for the tile's title. Otherwise, the 3 letter day of week
 * will be used from the validTimeUtc.
 */
export const DailyDayForecastTile = forwardRef(
  (
    {
      index,
      dayName,
      validTimeUtc,
      iconCodeExtend,
      wxPhraseLong,
      dayTemp,
      nightTemp,
      precipChance,
      onPress,
      isLoading,
      error,
      hasTVPreferredFocus,
      nativeID,
      testID,
      nextFocusDown,
      nextFocusLeft,
      nextFocusRight,
      nextFocusUp,
      onFocus,
    },
    reference,
  ) => {
    const { t } = useTranslation();

    const { timeZone } = useRecoilValue(dayDateTimeState);

    let dateString;
    let tileTitleDay;

    if (!isLoading && !error && validTimeUtc) {
      const date = TimeUtils.dateFromEpoch(validTimeUtc);

      dateString = TimeUtils.getZonedDate(date, timeZone);
      tileTitleDay = index < 2 ? dayName : TimeUtils.dayOfWeekAbbr(date);
    }
    const isNight = !dayTemp;
    const precipChanceRounded = precipChance ? roundNearest5(precipChance) : null;

    return (
      <FocusableHighlight
        accessibilityHint={t(`${tPath}tileAccessibilityHint`)}
        accessibilityLabel={
          !isLoading && !error
            ? isNight
              ? t(`${tPath}nightTileAccessibilityLabel`, {
                  day: dayName,
                  date: dateString,
                  weather: wxPhraseLong,
                  nighttemp: nightTemp,
                  precip: precipChanceRounded,
                })
              : t(`${tPath}tileAccessibilityLabel`, {
                  day: dayName,
                  date: dateString,
                  weather: wxPhraseLong,
                  daytemp: dayTemp,
                  nighttemp: nightTemp,
                  precip: precipChanceRounded,
                })
            : null
        }
        hasTVPreferredFocus={hasTVPreferredFocus}
        nativeID={nativeID || testID}
        nextFocusDown={nextFocusDown}
        nextFocusLeft={nextFocusLeft}
        nextFocusRight={nextFocusRight}
        nextFocusUp={nextFocusUp}
        ref={reference}
        style={styles.highlight}
        testID={testID}
        underlayColor={Theme.transparent}
        onFocus={onFocus}
        onPress={onPress}
      >
        <ForecastTileContent
          date={dateString}
          dayName={tileTitleDay}
          dayTemp={dayTemp}
          error={error}
          iconCodeExtend={iconCodeExtend}
          isLoading={isLoading}
          nightTemp={nightTemp}
          precipChance={precipChanceRounded}
        />
      </FocusableHighlight>
    );
  },
);

DailyDayForecastTile.propTypes = {
  nativeID: PropTypes.string,
  onPress: PropTypes.func.isRequired,
  testID: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  dayName: PropTypes.string,
  validTimeUtc: PropTypes.number,
  iconCodeExtend: PropTypes.number,
  wxPhraseLong: PropTypes.string,
  dayTemp: PropTypes.number,
  nightTemp: PropTypes.number,
  precipChance: PropTypes.number,
  index: PropTypes.number,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  hasTVPreferredFocus: PropTypes.bool,
  nextFocusDown: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusLeft: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusRight: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusUp: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  onFocus: PropTypes.func,
};

DailyDayForecastTile.defaultProps = {
  nativeID: null,
  isLoading: false,
  dayName: null,
  validTimeUtc: null,
  iconCodeExtend: null,
  wxPhraseLong: '',
  dayTemp: null,
  nightTemp: null,
  precipChance: null,
  index: null,
  error: null,
  hasTVPreferredFocus: false,
  nextFocusDown: null,
  nextFocusLeft: null,
  nextFocusRight: null,
  nextFocusUp: null,
  onFocus: () => {},
};

const SIZES = {
  borderWidth: 6,
  borderRadius: 19,
  componentRadius: 12,
  componentWidth: 240,
  componentHeight: 398,
  topArrowWidth: 48,
  topArrowHight: 20,
  bottomTextHeight: 30,
  degreeSymbolWidthLg: 19,
  degreeSymbolWidthSm: 10,
};

const styles = StyleSheet.create({
  centerContainer: {
    alignItems: 'center',
    height: '100%',
    justifyContent: 'center',
    width: '100%',
  },
  container: {
    alignItems: 'center',
    backgroundColor: Theme.withOpacity(Theme.whisperWhite, 0.05),
    borderRadius: Theme.tileBorderRadius,
    flexDirection: 'column',
    height: Theme.px(SIZES.componentHeight),
    justifyContent: 'flex-start',
    width: Theme.px(SIZES.componentWidth),
  },
  containerFocus: {
    alignItems: 'center',
    backgroundColor: Theme.withOpacity(Theme.whisperWhite, 0.05),
    borderColor: Theme.whisperWhite,
    borderLeftWidth: Theme.px(SIZES.borderWidth),
    borderRightWidth: Theme.px(SIZES.borderWidth),
    borderTopLeftRadius: Theme.tileBorderRadius,
    borderTopRightRadius: Theme.tileBorderRadius,
    borderTopWidth: Theme.px(SIZES.borderWidth),
    flexDirection: 'column',
    height: Theme.px(SIZES.componentHeight + SIZES.borderWidth),
    justifyContent: 'flex-start',
    width: Theme.px(SIZES.componentWidth + SIZES.borderWidth * 2),
  },
  errorText: {
    textAlign: 'center',
  },
  focusItemsContainer: {
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  highlight: {
    alignItems: 'center',
    flexDirection: 'column',
    height: Theme.px(SIZES.componentHeight + SIZES.borderWidth * 3 + SIZES.bottomTextHeight + SIZES.topArrowHight),
    justifyContent: 'center',
    width: Theme.px(SIZES.componentWidth + SIZES.borderWidth * 2),
  },
  nightTemp: {
    color: Theme.secondaryText,
    lineHeight: Theme.px(35),
    marginTop: Theme.px(7),
  },
  precipContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: Theme.px(21),
  },
  precipText: {
    color: Theme.mainText,
    marginLeft: Theme.px(6),
  },
  tempText: {
    lineHeight: Theme.px(77),
    marginTop: Theme.px(18),
  },
  tileHeader: {
    lineHeight: Theme.px(42),
    marginTop: Theme.px(19),
  },
  tileSubheader: {
    marginBottom: Theme.px(15),
  },
  topArrowFocus: {
    borderBottomColor: Theme.whisperWhite,
    borderBottomWidth: Theme.px(SIZES.topArrowHight),
    borderLeftColor: Theme.transparent,
    borderLeftWidth: Theme.px(SIZES.topArrowWidth / 2),
    borderRightColor: Theme.transparent,
    borderRightWidth: Theme.px(SIZES.topArrowWidth / 2),
    height: 0,
    width: 0,
  },
  topArrowHidden: {
    height: Theme.px(SIZES.topArrowHight),
    width: Theme.px(SIZES.topArrowWidth),
  },
  viewHourlyIndicator: {
    height: Theme.px(SIZES.bottomTextHeight + SIZES.borderWidth),
    opacity: 0,
    width: Theme.px(SIZES.componentWidth),
  },
  viewHourlyIndicatorFocused: {
    backgroundColor: Theme.whisperWhite,
    borderBottomLeftRadius: Theme.px(SIZES.borderRadius),
    borderBottomRightRadius: Theme.px(SIZES.borderRadius),
    borderColor: Theme.whisperWhite,
    borderWidth: Theme.px(SIZES.borderWidth),
    color: Theme.blackPearlBlue,
    height: Theme.px(SIZES.bottomTextHeight + SIZES.borderWidth * 2),
    lineHeight: Theme.px(SIZES.bottomTextHeight),
    textAlign: 'center',
    textAlignVertical: 'center',
    width: Theme.px(SIZES.componentWidth + SIZES.borderWidth * 2),
  },
});
